<template>
  <tr tabindex="0">
    <td
      style="word-break: break-word; font-size: 0px"
      align="center"
      :style="{ padding: padding }"
      class="cursor-move hover:outline-dashed hover:outline-offset-[-1px] hover:outline-gray-100"
      :class="{
        'outline-dashed outline-offset-[-1px] outline-gray-100': dragging,
      }"
    >
      <table
        role="presentation"
        cellpadding="0"
        cellspacing="0"
        style="border-collapse: separate"
        :align="block.attributes['align']"
        :width="width"
        border="0"
      >
        <tbody>
          <tr>
            <td
              :style="{ ...tdStyle, ...hoverTdStyle }"
              :align="block.attributes['align']"
              valign="middle"
              @mouseover="isHovered = true"
              @mouseleave="isHovered = false"
            >
              <div
                :key="buttonStyleKey"
                style="max-width: 100%"
                :style="buttonStyle"
                :href="block.attributes['href']"
                target="_blank"
                v-html="buttonContent"
              ></div>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { StyleValue, computed, ref } from "vue";

  const props = defineProps({
    block: {
      type: Object,
      required: true,
    },
    sectionIndex: {
      type: Number,
      required: true,
    },
    columnIndex: {
      type: Number,
      required: true,
    },
    blockIndex: {
      type: Number,
      required: true,
    },
    loopColumnIndex: {
      type: Number,
      required: true,
    },
    loopBlockIndex: {
      type: Number,
      required: true,
    },
    dragging: {
      type: Boolean,
      default: false,
    },
  });

  const mjmlStore = useMjmlStore();

  const isHovered = ref(false);

  const buttonContent = computed({
    get() {
      return props.block.content;
    },
    set(val: string) {
      mjmlStore.updateLoopBlockContent({
        value: val,
        loopBlockIndex: props.loopBlockIndex,
        loopColumnIndex: props.loopColumnIndex,
        blockIndex: props.blockIndex,
        columnIndex: props.columnIndex,
        sectionIndex: props.sectionIndex,
      });
    },
  });

  const buttonStyle = computed(() => {
    return {
      margin: innerPadding.value,
      color:
        isHovered.value && hoverColor.value ? hoverColor.value : color.value,
      display: "inline-block",
      textDecoration: textDecoration.value,
      lineHeight: "100%",
      fontSize: fontSize.value,
      fontFamily: fontFamily.value,
      fontWeight: fontWeigth.value,
      textTransform: textTransform.value,
      fontStyle: fontStyle.value,
      textAlign: textAlign.value,
    };
  });

  const textAlign = computed(() => {
    return props.block.attributes["text-align"] || "center";
  });

  const buttonStyleKey = computed(() => {
    return JSON.stringify(buttonStyle.value);
  });

  const bgColor = computed(() => {
    return props.block.attributes["background-color"]
      ? props.block.attributes["background-color"]
      : "#414141";
  });

  const hoverBgColor = computed(() => {
    return props.block["hover"]?.["background-color"];
  });

  const gradient = computed(() => {
    return props.block.attributes["gradient"]
      ? props.block.attributes["gradient"]
      : "";
  });

  const hoverGradient = computed(() => {
    return props.block["hover"]?.gradient;
  });

  const color = computed(() => {
    return props.block.attributes["color"]
      ? props.block.attributes["color"]
      : "#ffffff";
  });

  const hoverColor = computed(() => {
    return props.block["hover"]?.color;
  });

  const padding = computed(() => {
    return props.block.attributes["padding"]
      ? props.block.attributes["padding"]
      : "10px 25px 10px 25px";
  });

  const innerPadding = computed(() => {
    const pad = props.block.attributes["inner-padding"];
    if (pad) {
      return pad;
    } else {
      return "10px 25px 10px 25px";
    }
  });

  const fontSize = computed(() => {
    return props.block.attributes["font-size"] || "13px";
  });

  const textDecoration = computed(() => {
    return props.block.attributes["text-decoration"]
      ? props.block.attributes["text-decoration"]
      : "none";
  });

  const textTransform = computed(() => {
    return props.block.attributes["text-transform"]
      ? props.block.attributes["text-transform"]
      : "none";
  });

  const fontStyle = computed(() => {
    return props.block.attributes["font-style"]
      ? props.block.attributes["font-style"]
      : "normal";
  });

  const fontWeigth = computed(() => {
    return props.block.attributes["font-weight"]
      ? props.block.attributes["font-weight"]
      : "normal";
  });

  const fontFamily = computed(() => {
    return props.block.attributes["font-family"]
      ? props.block.attributes["font-family"]
      : "Helvetica, Arial, sans-serif";
  });

  const tdStyle = computed(() => {
    return {
      backgroundColor: bgColor.value,
      border: "none",
      "border-radius": borderRadius.value,
      backgroundImage: gradient.value,
    };
  });

  const hoverTdStyle = computed(() => {
    if (!isHovered.value) {
      return {};
    }
    return {
      backgroundColor: hoverBgColor.value ? hoverBgColor.value : bgColor.value,
      backgroundImage: hoverGradient.value
        ? hoverGradient.value
        : gradient.value,
    };
  });

  const width = computed(() => {
    return props.block.attributes["width"]
      ? props.block.attributes["width"]
      : "auto";
  });

  const borderRadius = computed(() => {
    if (props.block.attributes["border-radius"] == "none") {
      return 0;
    } else {
      return props.block.attributes["border-radius"];
    }
  });
</script>
